import React, { useEffect, useState } from 'react';
import { X, Share, Plus } from 'lucide-react';
import { useAppStore } from '../../stores/useAppStore';

const DISMISS_KEY = 'pwa-install-dismissed-v1';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface InstallPromptProps {
  /** Force the prompt visible regardless of timing heuristics (e.g. onboarding success screen) */
  forceVisible?: boolean;
}

export const InstallPrompt: React.FC<InstallPromptProps> = ({ forceVisible = false }) => {
  const theme = useAppStore((s) => s.theme);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
      return;
    }
    // @ts-ignore — non-standard Safari property
    if (window.navigator.standalone === true) {
      setInstalled(true);
      return;
    }

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    // Check dismiss cooldown
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const elapsed = Date.now() - parseInt(dismissed, 10);
      if (elapsed < DISMISS_DURATION_MS) return;
    }

    if (ios) {
      // iOS: show if forceVisible or after brief delay
      if (forceVisible) setShow(true);
      return;
    }

    // Android/Chrome: read prompt captured before React mounted
    const existing = (window as any).__deferredPWAInstall;
    if (existing) {
      setDeferredPrompt(existing);
      if (forceVisible) setShow(true);
    }

    // Also listen for future fires
    const handler = (e: Event) => {
      e.preventDefault();
      (window as any).__deferredPWAInstall = e;
      setDeferredPrompt(e);
      if (forceVisible) setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler as EventListener);

    if (forceVisible && isIOS) setShow(true);

    return () => window.removeEventListener('beforeinstallprompt', handler as EventListener);
  }, [forceVisible]);

  // When forceVisible changes to true, show if conditions are met
  useEffect(() => {
    if (!forceVisible || installed) return;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const elapsed = Date.now() - parseInt(dismissed, 10);
      if (elapsed < DISMISS_DURATION_MS) return;
    }
    if (isIOS || deferredPrompt) setShow(true);
  }, [forceVisible, isIOS, deferredPrompt, installed]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.removeItem(DISMISS_KEY);
    }
    setDeferredPrompt(null);
    setShow(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show || installed) return null;

  const isDark = theme === 'dark' || theme === 'neon';
  const cardBg = isDark ? 'bg-[#0E1530] border-[#1E2748]' : 'bg-white border-gray-200';
  const textColor = isDark ? 'text-[#E8ECF7]' : 'text-gray-900';
  const subtextColor = isDark ? 'text-[#A9B0CF]' : 'text-gray-500';

  if (isIOS) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className={`w-full max-w-sm rounded-2xl border ${cardBg} p-6 shadow-2xl`}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <img src="/android-chrome-192x192.png" alt="Levelix Class" className="w-12 h-12 rounded-xl" />
              <div>
                <p className={`font-bold text-base ${textColor}`}>Install Levelix Class</p>
                <p className={`text-xs ${subtextColor}`}>Add to your Home Screen</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-[#1E2748]' : 'hover:bg-gray-100'}`}
              aria-label="Dismiss"
            >
              <X className={`w-4 h-4 ${subtextColor}`} />
            </button>
          </div>

          <div className={`space-y-3 mb-5 text-sm ${textColor}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-xs">1</span>
              </div>
              <p>Tap the <Share className="inline w-4 h-4 text-blue-500" /> <strong>Share</strong> button at the bottom of Safari</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-xs">2</span>
              </div>
              <p>Scroll down and tap <strong>"Add to Home Screen"</strong></p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                <Plus className="w-4 h-4 text-white" />
              </div>
              <p>Tap <strong>"Add"</strong> to confirm</p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="w-full py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  // Android / Chrome banner
  return (
    <div className={`fixed bottom-20 left-3 right-3 md:bottom-6 md:left-auto md:right-6 md:w-80 z-[9999] rounded-2xl border ${cardBg} shadow-2xl p-4`}>
      <div className="flex items-center gap-3">
        <img src="/android-chrome-192x192.png" alt="Levelix Class" className="w-10 h-10 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${textColor}`}>Install Levelix Class App</p>
          <p className={`text-xs ${subtextColor} truncate`}>Faster access, works offline</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleInstall}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors"
          >
            Install
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className={`p-1 rounded-lg ${isDark ? 'hover:bg-[#1E2748]' : 'hover:bg-gray-100'}`}
            aria-label="Dismiss"
          >
            <X className={`w-4 h-4 ${subtextColor}`} />
          </button>
        </div>
      </div>
    </div>
  );
};
